/**
 * One Game Summary stat block — Offense, Defense or Special Teams
 * (FR-SUMMARY-04, FR-SUMMARY-05, FR-SUMMARY-06).
 *
 * `GET /stats/v1/summary/{event_id}` projects each block as a bare
 * `stat_key → value` map: the server already decided *which* keys a tenant
 * sees (`show_in_game_summary` plus per-tenant `projectionOverrides`) but it
 * does not ship labels. `SUMMARY_STAT_ROWS` below is the display registry that
 * mirrors the `show_in_game_summary` subset of `DERIVED_STAT_CATALOG` in
 * `services/stats/src/aggregation/stat-catalog.ts` — it fixes the label,
 * category grouping and row order. Keys that arrive without a registry entry
 * (a tenant override switched one on) still render, humanised, at the end of an
 * "Other" group, so the page never silently drops a number.
 *
 * Fanatic projection: `isSummaryOnly` drops every `list`-format row, i.e. the
 * per-attempt detail (which kick, by whom, from where). Fanatics keep the
 * team-aggregate scalars, per `security/data-protection`.
 */
import { UNIT_TITLES } from '../../components/odst-segmented-toggle';
import type { StatUnit, StatValueFormat, SummaryStatValues } from '../../lib/api-types';

export interface SummaryStatRow {
  key: string;
  label: string;
  category: string;
  format: StatValueFormat;
}

type Row = [key: string, label: string, category: string, format: StatValueFormat];

function rows(entries: readonly Row[]): SummaryStatRow[] {
  return entries.map(([key, label, category, format]) => ({ key, label, category, format }));
}

/** Every stat FR-SUMMARY-04..06 enumerates, in reading order. */
export const SUMMARY_STAT_ROWS: Record<StatUnit, readonly SummaryStatRow[]> = {
  offense: rows([
    ['passing_yards', 'Passing Yards', 'Passing', 'yards'],
    ['completions', 'Completions', 'Passing', 'count'],
    ['attempts', 'Attempts', 'Passing', 'count'],
    ['completion_percent', 'Completion %', 'Passing', 'percent'],
    ['passing_yards_per_attempt', 'Passing Yards / Attempt', 'Passing', 'average'],
    ['passing_tds', 'Passing TDs', 'Passing', 'count'],
    ['interceptions', 'INTs', 'Passing', 'count'],
    ['qb_sacks', 'QB Sacks', 'Passing', 'count'],
    ['drops', 'Drops', 'Passing', 'count'],
    ['rushing_yards', 'Rushing Yards', 'Rushing', 'yards'],
    ['rushing_attempts', 'Rushing Attempts', 'Rushing', 'count'],
    ['rushing_yards_per_attempt', 'Rushing Yards / Attempt', 'Rushing', 'average'],
    ['rushing_tds', 'Rushing TDs', 'Rushing', 'count'],
    ['fumbles', 'Fumbles', 'Ball Security', 'count'],
    ['turnovers', 'Turnovers', 'Ball Security', 'count'],
    ['off_penalties', 'OFF Penalties', 'Discipline', 'count'],
  ]),
  defense: rows([
    ['sacks', 'Sacks', 'Pressure', 'count'],
    ['tackles_for_loss', 'TFL', 'Pressure', 'count'],
    ['takeaways', 'Takeaways', 'Takeaways', 'count'],
    ['def_interceptions', 'INTs', 'Takeaways', 'count'],
    ['defensive_tds', 'DEF TDs', 'Scoring', 'count'],
    ['def_penalties', 'DEF Penalties', 'Discipline', 'count'],
  ]),
  special_teams: rows([
    ['fg_attempts', 'FG Attempts', 'Kicking', 'list'],
    ['fg_make_percent', 'FG Make %', 'Kicking', 'percent'],
    ['extra_point_attempts', 'Extra Point Attempts', 'Kicking', 'list'],
    ['extra_point_percent', 'Extra Point %', 'Kicking', 'percent'],
    ['opp_fg_make_percent', 'Opp. FG Make % (vs. our block unit)', 'Kicking', 'percent'],
    ['opp_extra_point_percent', 'Opp. Extra Point % (vs. our block unit)', 'Kicking', 'percent'],
    ['punts', 'Punts', 'Punting', 'list'],
    ['punt_average', 'Punt Average', 'Punting', 'average'],
    ['punt_returns', 'Punt Returns', 'Returns', 'list'],
    ['punt_return_average', 'Punt Return Average', 'Returns', 'average'],
    ['kickoff_returns', 'KO Returns', 'Returns', 'list'],
    ['st_touchdowns', 'ST TDs', 'Returns', 'list'],
    ['st_penalties', 'ST Penalties', 'Discipline', 'count'],
  ]),
};

const ACRONYMS: Record<string, string> = {
  td: 'TD',
  tds: 'TDs',
  int: 'INT',
  ints: 'INTs',
  fg: 'FG',
  fgs: 'FGs',
  tfl: 'TFL',
  qb: 'QB',
  ko: 'KO',
  st: 'ST',
  off: 'OFF',
  def: 'DEF',
  pat: 'PAT',
  opp: 'Opp',
  percent: '%',
  avg: 'Avg',
};

/** `punt_return_average` → "Punt Return Average"; `def_tds` → "DEF TDs". */
export function humanizeStatKey(key: string): string {
  const words = key.split(/[_.\s]+/).filter((part) => part !== '');
  if (words.length === 0) return key;
  return words
    .map((word) => ACRONYMS[word.toLowerCase()] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(' %', ' %');
}

function formatNumber(value: number, format: StatValueFormat): string {
  if (format === 'percent') return `${value.toFixed(1)}%`;
  if (format === 'average') return value.toFixed(1);
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(1);
}

/**
 * Scalar renderer. `null`/`undefined` become an em dash rather than `0`: "no
 * field goals attempted" and "0 % on field goals" are different facts.
 */
export function formatStatValue(value: unknown, format: StatValueFormat): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return Number.isFinite(value) ? formatNumber(value, format) : '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return String(value.length);
  if (typeof value === 'string') return value.trim() === '' ? '—' : value;
  return '—';
}

/** One attempt on a `list`-format row, e.g. a field goal or a punt. */
export interface StatListEntry {
  label: string;
  isSuccess: boolean | null;
  note: string | null;
}

function entryRecord(entry: unknown): Record<string, unknown> {
  return typeof entry === 'object' && entry !== null ? (entry as Record<string, unknown>) : {};
}

function successOf(record: Record<string, unknown>): boolean | null {
  if (typeof record.make === 'boolean') return record.make;
  if (typeof record.success === 'boolean') return record.success;
  return null;
}

function noteOf(record: Record<string, unknown>): string | null {
  if (record.inside_20 === true) return 'inside 20';
  if (record.is_touchdown === true) return 'TD';
  if (typeof record.type === 'string') return record.type === 'two_point' ? '2-pt' : 'PAT';
  if (typeof record.play_type === 'string') return humanizeStatKey(record.play_type);
  return null;
}

/** Normalises the heterogeneous special-teams attempt records to one shape. */
export function toStatListEntries(value: unknown): StatListEntry[] {
  if (!Array.isArray(value)) return [];
  return value.map((entry, index) => {
    const record = entryRecord(entry);
    const yardage = typeof record.yardage === 'number' ? record.yardage : null;
    const quarter = typeof record.quarter === 'number' ? record.quarter : null;
    return {
      label: yardage === null ? `#${index + 1}` : `${yardage} yd`,
      isSuccess: successOf(record),
      note: noteOf(record) ?? (quarter === null ? null : `Q${quarter}`),
    };
  });
}

function StatListValue({ entries }: { entries: StatListEntry[] }): JSX.Element {
  if (entries.length === 0) return <span className="stat-block__empty">None</span>;
  return (
    <ul className="stat-block__list">
      {entries.map((entry, index) => (
        <li
          key={`${entry.label}-${index}`}
          className="stat-block__chip"
          data-outcome={entry.isSuccess === null ? 'neutral' : entry.isSuccess ? 'made' : 'missed'}
        >
          <span className="num">{entry.label}</span>
          {entry.isSuccess === null ? null : (
            <span aria-hidden="true">{entry.isSuccess ? '✓' : '✗'}</span>
          )}
          {entry.note === null ? null : <span className="stat-block__note">{entry.note}</span>}
          {entry.isSuccess === null ? null : (
            <span className="visually-hidden">{entry.isSuccess ? 'made' : 'missed'}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Registry rows present in the payload, plus any unregistered extras. */
export function resolveStatRows(
  unit: StatUnit,
  stats: SummaryStatValues,
  isSummaryOnly: boolean
): SummaryStatRow[] {
  const registry = SUMMARY_STAT_ROWS[unit];
  const known = new Set(registry.map((row) => row.key));
  const extras: SummaryStatRow[] = Object.keys(stats)
    .filter((key) => !known.has(key))
    .map((key) => ({
      key,
      label: humanizeStatKey(key),
      category: 'Other',
      format: Array.isArray(stats[key]) ? 'list' : 'count',
    }));
  return [...registry.filter((row) => row.key in stats), ...extras].filter(
    (row) => !isSummaryOnly || row.format !== 'list'
  );
}

function groupByCategory(all: readonly SummaryStatRow[]): [string, SummaryStatRow[]][] {
  const groups = new Map<string, SummaryStatRow[]>();
  for (const row of all) {
    const bucket = groups.get(row.category);
    if (bucket === undefined) groups.set(row.category, [row]);
    else bucket.push(row);
  }
  return [...groups.entries()];
}

export interface StatBlockProps {
  unit: StatUnit;
  /** `undefined` while loading, or when the unit was filtered out server-side. */
  stats: SummaryStatValues | undefined;
  /** Fanatic projection: team-aggregate scalars only, no per-attempt lists. */
  isSummaryOnly?: boolean;
  /** Skeleton on first paint only — a live refetch must never blank the sheet. */
  isLoading?: boolean;
  /** Renders an `<h2>` instead of an `<h3>` (desktop column layout). */
  headingLevel?: 2 | 3;
}

export function StatBlock({
  unit,
  stats,
  isSummaryOnly = false,
  isLoading = false,
  headingLevel = 3,
}: StatBlockProps): JSX.Element {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  // Category headings sit one level below the block title; hard-coding `h4`
  // skipped a level in the desktop column layout (axe `heading-order`).
  const CategoryHeading = headingLevel === 2 ? 'h3' : 'h4';
  const visible = stats === undefined ? [] : resolveStatRows(unit, stats, isSummaryOnly);

  return (
    <section
      className="stat-block"
      data-testid={`stat-block-${unit}`}
      aria-label={`${UNIT_TITLES[unit]} statistics`}
    >
      <Heading className="stat-block__title">{UNIT_TITLES[unit]}</Heading>
      {isLoading && stats === undefined ? (
        <p className="stat-block__empty" role="status">
          Loading {UNIT_TITLES[unit].toLowerCase()} stats…
        </p>
      ) : visible.length === 0 ? (
        <p className="stat-block__empty">
          No {UNIT_TITLES[unit].toLowerCase()} stats yet. They appear as plays are logged.
        </p>
      ) : (
        <div className="stat-block__groups">
          {groupByCategory(visible).map(([category, group]) => (
            <div key={category} className="stat-block__group">
              <CategoryHeading className="stat-block__category">{category}</CategoryHeading>
              <dl className="stat-block__rows">
                {group.map((row) => (
                  <div key={row.key} className="stat-block__row" data-stat-key={row.key}>
                    <dt className="stat-block__label">{row.label}</dt>
                    <dd className="stat-block__value">
                      {row.format === 'list' ? (
                        <StatListValue entries={toStatListEntries(stats?.[row.key])} />
                      ) : (
                        <span className="stat-block__number num">
                          {formatStatValue(stats?.[row.key], row.format)}
                        </span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
      {isSummaryOnly ? (
        <p className="stat-block__gate">
          Team totals only — per-attempt detail is limited to team staff.
        </p>
      ) : null}
    </section>
  );
}
